import { useState, FormEvent, useEffect, useRef, MouseEvent, KeyboardEvent } from 'react';
import { useFeedbackContext } from './FeedbackContext';
import { useFeedback } from './useFeedback';

export function FeedbackForm() {
  const { isOpen, closeForm, config, screenshot } = useFeedbackContext();
  const { submit, isLoading, isSuccess, isError, errorMessage, successMessage } = useFeedback();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);

  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset local form state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setIncludeScreenshot(true);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    await submit({
      title: title.trim(),
      description: description.trim(),
      includeScreenshot: includeScreenshot && !!screenshot,
    });
    if (!isError) {
      setTitle('');
      setDescription('');
    }
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      closeForm();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !isLoading) {
      closeForm();
    }
  };

  if (!isOpen) return null;

  const baseZIndex = config.zIndex ?? 9999;

  // Dark theme colors
  const colors = {
    background: 'var(--pm-bg-base)',
    card: 'var(--pm-bg-card)',
    foreground: 'var(--pm-text-primary)',
    mutedForeground: 'var(--pm-text-muted)',
    border: 'var(--pm-border-default)',
    input: 'var(--pm-bg-base)',
    primary: 'var(--pm-cyan)',
    primaryHover: 'var(--pm-cyan-dim)',
    primaryForeground: 'var(--pm-btn-primary-text)',
    success: '#10b981',
    successBg: 'var(--pm-green-bg)',
    error: '#ef4444',
    errorBg: '#3d1f1f',
    buttonGray: 'var(--pm-bg-card-alt)',
    buttonGrayHover: 'var(--pm-border-default)',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--pm-bg-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: baseZIndex + 1,
        padding: '16px',
      }}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-form-title"
    >
      <div
        style={{
          backgroundColor: colors.card,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          boxShadow: '0 25px 50px -12px var(--pm-shadow-strong)',
          width: '100%',
          maxWidth: '400px',
          maxHeight: '90vh',
          overflow: 'auto',
          ...config.formStyle,
        }}
      >
        <div style={{ padding: '32px 28px 28px' }}>
          {/* Microphone Icon in rounded square */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '12px',
                border: `2px solid ${colors.primary}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke={colors.primary}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" x2="12" y1="19" y2="22" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h2
            id="feedback-form-title"
            style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: 600,
              color: colors.foreground,
              textAlign: 'center',
            }}
          >
            Something's off
          </h2>

          {/* Subtitle */}
          <p
            style={{
              margin: '0 0 28px 0',
              fontSize: '14px',
              color: colors.mutedForeground,
              textAlign: 'center',
            }}
          >
            Give us the details
          </p>

          {/* Success message */}
          {isSuccess && successMessage && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: colors.successBg,
                border: `1px solid ${colors.success}`,
                borderRadius: '8px',
                marginBottom: '16px',
                color: colors.success,
                fontSize: '14px',
                textAlign: 'center',
              }}
              role="status"
            >
              {successMessage}
            </div>
          )}

          {/* Error message */}
          {isError && errorMessage && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: colors.errorBg,
                border: `1px solid ${colors.error}`,
                borderRadius: '8px',
                marginBottom: '16px',
                color: colors.error,
                fontSize: '14px',
                textAlign: 'center',
              }}
              role="alert"
            >
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Title input */}
            <div style={{ marginBottom: '12px' }}>
              <input
                ref={titleInputRef}
                id="feedback-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's happening?"
                required
                disabled={isLoading || isSuccess}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '14px',
                  backgroundColor: colors.input,
                  color: colors.foreground,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary;
                  e.target.style.boxShadow = `0 0 0 3px var(--pm-cyan-border-muted)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Description textarea */}
            <div style={{ marginBottom: '20px' }}>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell us more..."
                required
                disabled={isLoading || isSuccess}
                rows={4}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '14px',
                  backgroundColor: colors.input,
                  color: colors.foreground,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '120px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary;
                  e.target.style.boxShadow = `0 0 0 3px var(--pm-cyan-border-muted)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Screenshot section */}
            {screenshot && (
              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                  }}
                >
                  <input
                    type="checkbox"
                    id="include-screenshot"
                    checked={includeScreenshot}
                    onChange={(e) => setIncludeScreenshot(e.target.checked)}
                    disabled={isLoading || isSuccess}
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: colors.primary,
                      cursor: isLoading || isSuccess ? 'not-allowed' : 'pointer',
                    }}
                  />
                  <label
                    htmlFor="include-screenshot"
                    style={{
                      fontSize: '14px',
                      color: colors.mutedForeground,
                      cursor: isLoading || isSuccess ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Include screenshot
                  </label>
                </div>
                {includeScreenshot && (
                  <div
                    style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.background,
                    }}
                  >
                    <img
                      src={screenshot}
                      alt="Screenshot preview"
                      style={{
                        display: 'block',
                        width: '100%',
                        maxHeight: '150px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                onClick={closeForm}
                disabled={isLoading}
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: colors.foreground,
                  backgroundColor: colors.buttonGray,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = colors.buttonGrayHover;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.buttonGray;
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || isSuccess || !title.trim() || !description.trim()}
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: colors.primaryForeground,
                  backgroundColor: isLoading || isSuccess || !title.trim() || !description.trim()
                    ? 'var(--pm-cyan-dim)'
                    : colors.primary,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading || isSuccess || !title.trim() || !description.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s',
                  opacity: isLoading || isSuccess || !title.trim() || !description.trim() ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && !isSuccess && title.trim() && description.trim()) {
                    e.currentTarget.style.backgroundColor = colors.primaryHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading && !isSuccess && title.trim() && description.trim()) {
                    e.currentTarget.style.backgroundColor = colors.primary;
                  }
                }}
              >
                {isLoading && (
                  <svg
                    style={{
                      width: '16px',
                      height: '16px',
                      animation: 'feedback-spin 1s linear infinite',
                    }}
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      opacity="0.25"
                    />
                    <path
                      d="M12 2a10 10 0 0 1 10 10"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                {isLoading ? 'Submitting...' : isSuccess ? 'Submitted!' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>
        {`
          @keyframes feedback-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
